'use client';

import { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
var VideoPlayerErrorType = /*#__PURE__*/function (VideoPlayerErrorType) {
  VideoPlayerErrorType[VideoPlayerErrorType["MEDIA_ERR_CUSTOM"] = 0] = "MEDIA_ERR_CUSTOM";
  VideoPlayerErrorType[VideoPlayerErrorType["MEDIA_ERR_ABORTED"] = 1] = "MEDIA_ERR_ABORTED";
  VideoPlayerErrorType[VideoPlayerErrorType["MEDIA_ERR_NETWORK"] = 2] = "MEDIA_ERR_NETWORK";
  VideoPlayerErrorType[VideoPlayerErrorType["MEDIA_ERR_DECODE"] = 3] = "MEDIA_ERR_DECODE";
  VideoPlayerErrorType[VideoPlayerErrorType["MEDIA_ERR_SRC_NOT_SUPPORTED"] = 4] = "MEDIA_ERR_SRC_NOT_SUPPORTED";
  VideoPlayerErrorType[VideoPlayerErrorType["MEDIA_ERR_ENCRYPTED"] = 5] = "MEDIA_ERR_ENCRYPTED";
  return VideoPlayerErrorType;
}(VideoPlayerErrorType || {});
;
const VideoJSPlayer = ({
  sources,
  options,
  poster,
  onReady,
  onPlay,
  onPause,
  onEnded,
  onError,
  onTimeout,
  onTimeUpdate,
  isPlaying,
  isHidden,
  loadTimeoutMs = 10000
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const playRequestRef = useRef(undefined);
  const loadTimeoutRef = useRef(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const getPlayer = useCallback(() => {
    const player = playerRef.current;
    return player && typeof player.isDisposed === 'function' && !player.isDisposed() ? player : null;
  }, []);
  const handlePlay = useCallback(async () => {
    const player_0 = getPlayer();
    if (!player_0) return;
    try {
      if (playRequestRef.current) {
        playRequestRef.current = undefined;
      }
      const playPromise = player_0.play();
      if (playRequestRef) {
        playRequestRef.current = playPromise;
      }
      await playPromise;
      if (playRequestRef.current === playPromise) {
        playRequestRef.current = undefined;
      }
    } catch (e) {
      if (playRequestRef.current === undefined) return;
      console.error('Failed to play video', e);
    }
    playRequestRef.current = undefined;
  }, []);
  const clearVideoLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);
  const startVideoLoadTimeout = useCallback(() => {
    clearVideoLoadTimeout();
    loadTimeoutRef.current = setTimeout(() => {
      onTimeout?.();
    }, loadTimeoutMs);
  }, [onTimeout, loadTimeoutMs, clearVideoLoadTimeout]);
  const handleError = useCallback(error => {
    onError?.({
      type: VideoPlayerErrorType.MEDIA_ERR_CUSTOM,
      message: 'VideoJS Error',
      originalError: error
    });
  }, [onError]);
  useEffect(() => {
    if (!videoRef.current) return;
    let player_1 = getPlayer();
    if (player_1) {
      playerRef.current = null;
      setIsPlayerReady(false);
      clearVideoLoadTimeout();
      try {
        player_1?.dispose();
      } catch (e_0) {
        console.error('Failed to dispose player', e_0);
      }
    }
    const initTimeout = setTimeout(() => {
      if (!videoRef.current) return;
      try {
        const player_2 = videojs(videoRef.current, {
          ...options,
          sources,
          html5: {
            vhs: {
              ...options.vhs,
              overrideNative: !videojs.browser.IS_SAFARI
            }
          }
        }, () => {
          setIsPlayerReady(true);
          onReady?.(player_2);
        });
        playerRef.current = player_2;
        player_2.on('play', () => onPlay?.());
        player_2.on('pause', () => onPause?.());
        player_2.on('ended', () => onEnded?.());
        player_2.on('loadstart', startVideoLoadTimeout);
        player_2.on('loadeddata', clearVideoLoadTimeout);
        player_2.on('loadedmetadata', () => {
          clearVideoLoadTimeout();
          if (isPlaying) handlePlay();
        });
        player_2.on('error', error_0 => {
          console.log('VideoJS Error:', error_0);
          //handleError(VideoPlayerErrorType.MEDIA_ERR_CUSTOM, 'VideoJS Error', error);
        });
        player_2.on('timeupdate', () => onTimeUpdate?.(player_2.currentTime(), player_2.duration()));
      } catch (e_1) {
        console.error('Failed to initialize player', e_1);
      }
    }, 50);
    return () => {
      startTransition(() => {
        clearTimeout(initTimeout);
        clearVideoLoadTimeout();
        playRequestRef.current = undefined;
        const player_3 = getPlayer();
        if (player_3) {
          playerRef.current = null;
          setIsPlayerReady(false);
          try {
            player_3?.dispose();
          } catch (e_2) {
            console.error('Failed to dispose player', e_2);
          }
        }
      });
    };
  }, []);
  useEffect(() => {
    const player_4 = getPlayer();
    if (!player_4 || !sources) return;
    try {
      playRequestRef.current = undefined;
      clearVideoLoadTimeout();
      startVideoLoadTimeout();
      player_4.src(sources);
    } catch (e_3) {
      clearVideoLoadTimeout();
      console.error('Failed to set video source', e_3);
    }
  }, [JSON.stringify(sources)]);
  useEffect(() => {
    if (!isPlayerReady) return;
    const player_5 = getPlayer();
    if (!player_5) return;
    if (isPlaying) {
      handlePlay();
    } else {
      player_5.pause();
    }
  }, [isPlaying, isPlayerReady]);
  useEffect(() => {
    if (!isPlayerReady) return;
    const player_6 = getPlayer();
    if (!player_6) return;
    try {
      if (isHidden) {
        player_6.pause();
        const videoEl = player_6.el()?.querySelector('video');
        if (videoEl) videoEl.style.display = 'none';
        const controlBar = player_6.el()?.querySelector('.vjs-control-bar');
        //if (controlBar) controlBar.style.display = 'none';
      } else {
        const videoEl_0 = player_6.el()?.querySelector('video');
        if (videoEl_0) videoEl_0.style.display = 'block';
        const controlBar_0 = player_6.el()?.querySelector('.vjs-control-bar');
        //if (controlBar) controlBar.style.display = 'flex';

        if (isPlaying) {
          handlePlay();
        }
      }
    } catch (e_4) {
      console.error('Failed to update player visibility', e_4);
    }
  }, [isHidden, isPlaying, isPlayerReady]);
  return <div className="video-js-container" style={{
    display: isHidden ? 'none' : 'block',
    width: '100%',
    height: '100%'
  }}>
      <div data-vjs-player>
        <video ref={videoRef} className="video-js vjs-big-play-centered" poster={poster} playsInline />
      </div>
    </div>;
};
export default VideoJSPlayer;